import Agriculture from '@mui/icons-material/Agriculture'
import Block from '@mui/icons-material/Block'
import Nature from '@mui/icons-material/Nature'
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline'
import Verified from '@mui/icons-material/Verified'
import { Box, Paper, Skeleton, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import {
  ELIGIBILITY_QUERY,
  useAttestationAreas,
  useCarbonInstances,
  useEligibilityCheck,
  useExclusionAreas,
} from '../../../hooks/data'
import { Farm } from '../../../types/apiModels'
import { COLORS } from '../../../utils/constants'
import { Map } from '../../Map'
import { TextWithLineBreaks } from '../../TextWithLineBreaks/TextWithLineBreaks'
import { AttestationWarningsToast } from './AttestationWarningsToast'

const StatCard = ({
  label,
  value,
  icon,
  color,
  bgColor,
}: {
  label: string
  value: string
  icon: React.ReactNode
  color: string
  bgColor: string
}) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      flex: '1 1 200px',
      minWidth: '200px',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        borderColor: color,
        boxShadow: `0 4px 12px ${bgColor}`,
        transform: 'translateY(-2px)',
      },
    }}
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '12px',
        bgcolor: bgColor,
        color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '1.1rem' }} variant="h6">
        {value}
      </Typography>
      <Typography color="text.secondary" sx={{ fontWeight: 500 }} variant="body2">
        {label}
      </Typography>
    </Box>
  </Paper>
)

export const AttestationCarbonInstances = ({
  disclaimer,
  farm,
  programMonitoringPeriodId,
}: {
  disclaimer?: string
  farm: Farm
  programMonitoringPeriodId: string
}) => {
  const { data: exclusionAreas, isLoading } = useExclusionAreas({
    farmId: farm.id,
  })
  const { eligibilityCheckAreas, isLoading: isLoadingEligibilityCheck } = useEligibilityCheck({
    farmId: farm.id,
    query: ELIGIBILITY_QUERY,
  })

  const carbonInstancesQuery = useMemo(() => {
    return {
      programMonitoringPeriodId,
      cumulative: true,
    }
  }, [programMonitoringPeriodId])

  const { carbonInstances, isLoading: isLoadingCarbonInstances } = useCarbonInstances({
    farmId: farm.id,
    query: carbonInstancesQuery,
  })

  // The /v2/farms/:farmId/attestation-areas endpoint is only mounted in a SARA context,
  // so this component (and therefore this hook call) is expected to run for SARA farms only.
  // If the attestation view is ever reused for other programs, gate this call by programId.
  const { areas, warnings, isLoading: isLoadingAreas } = useAttestationAreas(farm.id)

  const layers = useMemo(() => {
    const ci =
      carbonInstances?.map(instance => {
        return {
          ...instance,
          properties: {
            ...instance.properties,
            color: COLORS.CARBON_INSTANCE,
            type: 'carbon_instance',
            name: 'Área de programa',
          },
        }
      }) || []

    const eligibility =
      eligibilityCheckAreas?.map(area => {
        return {
          ...area,
          properties: {
            ...area.properties,
            name: area.properties.label,
          },
        }
      }) || []

    const exclusion =
      exclusionAreas?.map(area => {
        return {
          ...area,
          properties: {
            ...area.properties,
            name: 'Área de exclusión',
            color: 'yellow',
          },
        }
      }) || []

    return { carbonInstances: ci, eligibilityCheckAreas: eligibility, exclusionAreas: exclusion }
  }, [carbonInstances, eligibilityCheckAreas, exclusionAreas])

  const totals = useMemo(
    () =>
      areas
        ? {
            farm: areas.establishmentHa,
            program: areas.programHa,
            ineligible: areas.ineligibleHa,
            excluded: areas.excludedHa,
            offProgram: areas.offProgramHa,
          }
        : { farm: 0, program: 0, ineligible: 0, excluded: 0, offProgram: 0 },
    [areas],
  )

  if (isLoading || isLoadingEligibilityCheck || isLoadingCarbonInstances || isLoadingAreas)
    return <Skeleton />

  const perimeter = farm.toGeoJSON

  const formatHa = (val: number) =>
    val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  return (
    <>
      <AttestationWarningsToast warnings={warnings} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '16px' }}>
        <Typography sx={{ fontWeight: 'bold' }} typography="h5">
          Áreas de proyecto
        </Typography>

        <Box sx={{ mb: 1 }}>
          <Typography sx={{ fontWeight: 'bold', mb: 2 }} variant="subtitle1">
            Resumen de áreas
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <StatCard
              bgColor="#00000015"
              color="#000"
              icon={<Agriculture fontSize="medium" />}
              label="Establecimiento"
              value={`${formatHa(totals.farm)} ha`}
            />
            <StatCard
              bgColor={`${COLORS.CARBON_INSTANCE}15`}
              color={COLORS.CARBON_INSTANCE}
              icon={<Verified fontSize="medium" />}
              label="Área de programa"
              value={`${formatHa(totals.program)} ha`}
            />
            <StatCard
              bgColor="#FFA50015"
              color="#FFA500"
              icon={<Block fontSize="medium" />}
              label="Área no elegible"
              value={`${formatHa(totals.ineligible)} ha`}
            />
            <StatCard
              bgColor="#fbfb2d15"
              color="#fbfb2d"
              icon={<RemoveCircleOutline fontSize="medium" />}
              label="Área excluida"
              value={`${formatHa(totals.excluded)} ha`}
            />
            <StatCard
              bgColor="#9E9E9E15"
              color="#9E9E9E"
              icon={<Nature fontSize="medium" />}
              label="Fuera del programa"
              value={`${formatHa(totals.offProgram)} ha`}
            />
          </Box>
        </Box>
        <Map isImage flexDirection="column" mapHeight={650} mapWidth={750} perimeter={perimeter}>
          <Map.Layer layer={layers.exclusionAreas as unknown as GeoJSON.Feature} />
          <Map.Layer layer={layers.eligibilityCheckAreas as unknown as GeoJSON.Feature} />
          <Map.Layer layer={layers.carbonInstances as unknown as GeoJSON.Feature} />
          <Map.Legend
            layers={[
              ...(layers.carbonInstances[0] ? [layers.carbonInstances[0]] : []),
              ...layers.eligibilityCheckAreas,
              ...layers.exclusionAreas,
            ]}
          />
        </Map>
        <Typography variant="body2">
          <b>Nota:</b> Las áreas vacías en el mapa corresponden a áreas sin manejo, por lo tanto
          fuera del programa. Cuando una Carbon Instance, una capa no elegible y/o un área excluida
          se solapan, la prioridad para el cálculo de hectáreas es Carbon Instance &gt; Área no
          elegible &gt; Área excluida, manteniendo consistente el total con el área del
          establecimiento.
        </Typography>
        {disclaimer && (
          <Stack direction="column" spacing={2}>
            <Typography sx={{ textDecoration: 'underline' }} variant="h6">
              Atención:
            </Typography>
            <TextWithLineBreaks text={disclaimer} textAlign="left" />
          </Stack>
        )}
      </Box>
    </>
  )
}
